const PolicyContent = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="bg-[#FFF8F0] border-l-4 border-[#FF8C42] p-6 rounded-lg mb-12">
          <p className="text-[#2A2A2A] leading-relaxed">
            <strong>Effective Date:</strong> January 1, 2024
          </p>
          <p className="text-[#6B6B6B] mt-2">
            This Return Policy outlines the procedures and guidelines for freight returns, claims, and refusals for services provided by Sodrel Logistics LLC.
          </p>
        </div>

        <div className="space-y-12">
          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              1. Freight Inspection Upon Delivery
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-4">
              All freight shipments should be thoroughly inspected upon delivery. The consignee or authorized representative must:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Inspect all packages for visible damage before signing the delivery receipt</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Note any damage, shortage, or discrepancies on the delivery receipt</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Take photographs of damaged freight and packaging materials</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Retain all packaging materials for inspection if damage is discovered</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              2. Damage Claims
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-6">
              If freight arrives damaged or is discovered to be damaged after delivery, the following procedures must be followed:
            </p>
            
            <h3 className="text-xl font-semibold text-[#FF8C42] mb-3">
              Visible Damage
            </h3>
            <p className="text-[#6B6B6B] leading-relaxed mb-4">
              Damage that is apparent upon delivery must be noted on the delivery receipt. Claims for visible damage must be filed within 5 business days of delivery.
            </p>

            <h3 className="text-xl font-semibold text-[#FF8C42] mb-3 mt-6">
              Concealed Damage
            </h3>
            <p className="text-[#6B6B6B] leading-relaxed mb-4">
              Damage discovered after delivery and not visible during initial inspection must be reported within 15 days of delivery. Documentation including photographs and original packaging must be provided.
            </p>

            <div className="bg-[#FFF8F0] border-l-4 border-[#FF8C42] p-6 rounded-lg mt-6">
              <p className="text-[#2A2A2A] font-semibold mb-2">
                <i className="ri-information-line text-[#FF8C42] mr-2"></i>
                Important Note
              </p>
              <p className="text-[#6B6B6B]">
                All damaged freight and packaging materials must be retained until the claim is resolved. Disposal of materials may result in claim denial.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              3. Filing a Claim
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-4">
              To file a freight claim, please provide the following information:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Original bill of lading or pro number</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Detailed description of the damage or shortage</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Photographs of damaged items and packaging</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Original invoice or proof of value</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Signed delivery receipt noting damage</span>
              </li>
            </ul>
            <p className="text-[#6B6B6B] leading-relaxed mt-6">
              Claims can be submitted via email to <a href="mailto:sodrel@michaellogistics.com" className="text-[#FF8C42] font-semibold hover:underline cursor-pointer">sodrel@michaellogistics.com</a> or by calling <a href="tel:+13172460584" className="text-[#FF8C42] font-semibold hover:underline cursor-pointer">+1 (317) 246-0584</a>.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              4. Claim Processing Timeline
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-4">
              Sodrel Logistics LLC is committed to processing claims efficiently:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Acknowledgment of claim receipt within 2 business days</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Initial review and investigation within 10 business days</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Final resolution within 30 days of claim submission</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Payment issued within 15 days of claim approval</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              5. Refused Shipments
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-4">
              If a shipment is refused by the consignee:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">The shipper will be notified immediately</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Return shipping charges will be the responsibility of the shipper</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Storage fees may apply if freight is not retrieved within 5 business days</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Original shipping charges are non-refundable for refused shipments</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              6. Liability Limitations
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-4">
              Sodrel Logistics LLC's liability is subject to the following limitations:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Standard liability is limited to $0.50 per pound per article unless declared value is specified</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Excess valuation coverage is available for an additional fee</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Claims exceeding standard liability require proof of value documentation</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Consequential and indirect damages are not covered</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              7. Exceptions and Exclusions
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-4">
              The following circumstances are not covered under our return policy:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Damage resulting from improper packaging by the shipper</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Acts of God, natural disasters, or force majeure events</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Inherent vice or defect in the goods shipped</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Delays caused by incorrect or incomplete shipping information</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-[#FF8C42] rounded-full mr-3 mt-2"></span>
                <span className="text-[#6B6B6B]">Perishable goods that deteriorate during normal transit time</span>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2A2A2A] mb-6 pb-3 border-b-4 border-[#FF8C42] inline-block">
              8. Contact Information
            </h2>
            <p className="text-[#6B6B6B] text-lg leading-relaxed mb-6">
              For questions regarding our return policy or to file a claim, please contact us:
            </p>
            <div className="bg-gradient-to-r from-[#FF8C42] to-[#D4693A] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Questions About Returns?</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-phone-fill text-2xl mr-4"></i>
                  <div>
                    <p className="text-sm text-white/80">Phone</p>
                    <a href="tel:+13172460584" className="text-lg font-semibold hover:text-white/90 cursor-pointer">
                      +1 (317) 246-0584
                    </a>
                  </div>
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-fill text-2xl mr-4"></i>
                  <div>
                    <p className="text-sm text-white/80">Email</p>
                    <a href="mailto:sodrel@michaellogistics.com" className="text-lg font-semibold hover:text-white/90 cursor-pointer break-all">
                      sodrel@michaellogistics.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-map-pin-fill text-2xl mr-4 mt-1"></i>
                  <div>
                    <p className="text-sm text-white/80">Address</p>
                    <p className="text-lg font-semibold">
                      1 Sodrel Dr, Jeffersonville, IN 47129-2826
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PolicyContent;